import moment from "moment-timezone";

import { DATE_FORMATS } from "../../providerPayRequestDashboard/utils/constants";
import { TIME_ZONE } from "./payRequestConstants";

/*
 * Verbatim port of the mobile app's `src/utils/dateRangeHelper.ts`.
 *
 * Every boundary is computed in America/New_York and returned as a native Date,
 * so downstream comparisons against timesheet dates (also parsed in ET) line up
 * on the same instants.
 */
export const getPeriodDateRange = (referenceDate, periodType, contractStartDate = null) => {
  const date = moment(referenceDate);
  let startDate;
  let endDate;

  switch (periodType?.toLowerCase()) {
    case "daily": {
      startDate = date.clone().startOf("day");
      endDate = date.clone().endOf("day");
      break;
    }
    case "weekly": {
      const dayOfWeek = date.day();
      startDate = date.clone().subtract(dayOfWeek, "days").startOf("day");
      endDate = startDate.clone().add(6, "days").endOf("day");
      break;
    }
    case "bi-weekly":
    case "biweekly": {
      if (contractStartDate) {
        const refDate = moment.tz(contractStartDate, TIME_ZONE);
        const daysDiff = date.diff(refDate, "days");
        const cycleNumber = Math.floor(daysDiff / 14);
        startDate = refDate
          .clone()
          .add(cycleNumber * 14, "days")
          .startOf("day");
        endDate = startDate.clone().add(13, "days").endOf("day");
      } else {
        startDate = date.clone().startOf("month").startOf("day");
        const weekNum = Math.floor((date.date() - 1) / 14);
        startDate.add(weekNum * 14, "days");
        endDate = startDate.clone().add(13, "days").endOf("day");
      }
      break;
    }
    case "quarterly": {
      const quarter = Math.floor(date.month() / 3);
      startDate = date
        .clone()
        .month(quarter * 3)
        .startOf("month")
        .startOf("day");
      endDate = date
        .clone()
        .month(quarter * 3 + 2)
        .endOf("month")
        .endOf("day");
      break;
    }
    case "yearly":
      startDate = date.clone().startOf("year").startOf("day");
      endDate = date.clone().endOf("year").endOf("day");
      break;
    case "monthly":
    default:
      startDate = date.clone().startOf("month").startOf("day");
      endDate = date.clone().endOf("month").endOf("day");
      break;
  }

  return { startDate: startDate.toDate(), endDate: endDate.toDate() };
};

// Month bounds in server tz, matching mobile's getStartOfMonthInServerTz /
// getEndOfMonthInServerTz (workCalendar.ts:271-283).
export const getStartOfMonthInServerTz = (date) => {
  const d = moment(date);
  return moment.tz([d.year(), d.month(), d.date()], TIME_ZONE).startOf("month").format("YYYY-MM-DD HH:mm:ss");
};

export const getEndOfMonthInServerTz = (date) => {
  const d = moment(date);
  return moment.tz([d.year(), d.month(), d.date()], TIME_ZONE).endOf("month").format("YYYY-MM-DD HH:mm:ss");
};

/*
 * Window for the Hours Worked Trend card (graph2).
 *
 * get-hours-worked-trend rejects a null in_start_date ("Start Date is
 * required."), and the design wants a full multi-period line, so the range is
 * fixed rather than derived from today: the Month / Quarter / Year strip only
 * changes in_group_by. Move to a rolling window by editing this one constant.
 */
export const TREND_DATE_RANGE = {
  startDate: "2025-07-01 00:00:00",
  endDate: "2026-07-31 00:00:00",
};

/*
 * The calendar month, quarter or year containing referenceDate, as the
 * "YYYY-MM-DD HH:mm:ss" strings the stored procedures expect.
 *
 * get-pay-request-count-by-status takes no grouping parameter - it returns one
 * flat count per status - so its granularity strip moves this window instead.
 */
export const getGranularityRange = (referenceDate, granularity) => {
  const unit = granularity === "year" ? "year" : granularity === "quarter" ? "quarter" : "month";
  const date = moment.tz(referenceDate ?? new Date(), TIME_ZONE);

  return {
    startDate: date.clone().startOf(unit).format(DATE_FORMATS.API_FORMAT),
    endDate: date.clone().endOf(unit).format(DATE_FORMATS.API_FORMAT),
  };
};

export const getCurrentCalendarPeriod = (periods, granularity, referenceDate = new Date()) => {
  const unit = String(granularity ?? "")
    .toLowerCase()
    .replace(/ly$/, "");
  const today = moment.tz(referenceDate, TIME_ZONE);
  const currentPeriod = periods.find((period) => {
    const startDate = moment.tz(period.period_start_date, TIME_ZONE);
    if (!startDate.isValid()) return false;

    if (unit === "month" || unit === "quarter" || unit === "year") {
      return startDate.isSame(today, unit);
    }

    const endDate = moment.tz(period.period_end_date, TIME_ZONE);
    return (
      endDate.isValid() &&
      startDate.clone().startOf("day").isSameOrBefore(today.clone().startOf("day")) &&
      endDate.clone().endOf("day").isSameOrAfter(today.clone().startOf("day"))
    );
  });

  return currentPeriod || periods[0] || null;
};
